import { useEffect, useRef, useState } from 'react';

import type { KpiAxisConfiguration } from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import {
    clampKpiRange,
    type KpiDataRange,
} from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import { KPI_RANGE_GAP } from 'constants/ppa';

const KPI_INPUT_ERROR_DURATION_MS = 3000;

type UseKpiRangeInputParams = {
    dataRange: KpiDataRange;
    config: KpiAxisConfiguration;
    onChange: (patch: Partial<KpiAxisConfiguration>) => void;
    onRangeInteraction?: (source: 'input' | 'slider', min: number, max: number) => void;
};

export const useKpiRangeInput = ({ dataRange, config, onChange, onRangeInteraction }: UseKpiRangeInputParams) => {
    const [minInputError, setMinInputError] = useState<string | null>(null);
    const [maxInputError, setMaxInputError] = useState<string | null>(null);
    const minErrorTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const maxErrorTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(
        () => () => {
            if (minErrorTimeoutRef.current) clearTimeout(minErrorTimeoutRef.current);
            if (maxErrorTimeoutRef.current) clearTimeout(maxErrorTimeoutRef.current);
        },
        [],
    );

    const triggerInputError = (field: 'min' | 'max', message: string) => {
        const setError = field === 'min' ? setMinInputError : setMaxInputError;
        const timeoutRef = field === 'min' ? minErrorTimeoutRef : maxErrorTimeoutRef;

        setError(message);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => setError(null), KPI_INPUT_ERROR_DURATION_MS);
    };

    const getMinErrorMessage = (rawMin: number, rawMax: number): string | null => {
        const min = Math.round(rawMin);
        const max = Math.round(rawMax);
        if (min < dataRange.defaultMin) return `Minimum value is ${dataRange.defaultMin}`;
        if (min >= max) return 'Min must be less than Max';
        return null;
    };

    const getMaxErrorMessage = (rawMin: number, rawMax: number): string | null => {
        const min = Math.round(rawMin);
        const max = Math.round(rawMax);
        if (max > dataRange.defaultMax) return `Maximum value is ${dataRange.defaultMax}`;
        if (max <= min) return 'Min must be less than Max';
        return null;
    };

    const applyRange = (
        nextMin: number,
        nextMax: number,
        adjustedField?: 'min' | 'max',
        source?: 'input' | 'slider',
    ) => {
        let min = Math.round(nextMin);
        let max = Math.round(nextMax);
        let wasAdjusted = false;

        if (min < dataRange.defaultMin) {
            min = dataRange.defaultMin;
            wasAdjusted = true;
        }
        if (max > dataRange.defaultMax) {
            max = dataRange.defaultMax;
            wasAdjusted = true;
        }
        if (min > dataRange.defaultMax - KPI_RANGE_GAP) {
            min = dataRange.defaultMax - KPI_RANGE_GAP;
            wasAdjusted = true;
        }
        if (max < dataRange.defaultMin + KPI_RANGE_GAP) {
            max = dataRange.defaultMin + KPI_RANGE_GAP;
            wasAdjusted = true;
        }
        if (min >= max) {
            const clamped = clampKpiRange(min, max, dataRange);
            min = clamped.min;
            max = clamped.max;
            wasAdjusted = true;
        }

        if (wasAdjusted && adjustedField) {
            const errorMsg =
                adjustedField === 'min'
                    ? getMinErrorMessage(nextMin, nextMax)
                    : getMaxErrorMessage(nextMin, nextMax);
            if (errorMsg) triggerInputError(adjustedField, errorMsg);
        }

        const sanitizedBreakpoints = config.breakpoints.filter((value) => value > min && value < max);

        if (source) {
            onRangeInteraction?.(source, min, max);
        }

        onChange({
            min,
            max,
            breakpoints: sanitizedBreakpoints,
        });
    };

    const updateRangeFromSlider = (nextMin: number, nextMax: number) => {
        applyRange(nextMin, nextMax, undefined, 'slider');
    };

    return {
        minInputError,
        maxInputError,
        applyMinValue: (value: number) => applyRange(value, config.max, 'min', 'input'),
        applyMaxValue: (value: number) => applyRange(config.min, value, 'max', 'input'),
        updateRangeFromSlider,
    };
};
